import { getAuth } from "firebase/auth";
import { app } from "../firebase-config.js";
import { UserService } from "../services/user.service.js";
import { AlertModal } from "../components/modals/AlertModal.js";
import { InfoModal } from "../components/modals/InfoModal.js";
import { ContentService } from "../services/content.service.js";
import { PlazaService } from "../services/plaza.service.js";
import { UserListModal } from "../components/common/user-list-modal.js";
import { FeedCard } from "../components/feature/plaza/FeedCard.js";
import { AuthRequireModal } from "../components/modals/AuthRequireModal.js";
import { locale } from "../services/LocaleService.js";

// Note: We use window.user for the *current* logged in user, 
// but we fetch the *target* user profile passed via props.

export const PublicProfilePage = async (targetUserId) => {
    const container = document.createElement('div');
    container.className = 'max-w-3xl mx-auto bg-white min-h-screen pb-20 fade-in';

    // State
    const currentUser = window.user;
    let user = null; // The profile owner
    let isFollowing = false;
    let isProcessing = false; // Prevent rapid clicking
    let activeTab = 'ssulwha'; // Default tab

    // --- Data Loading ---
    const loadData = async () => {
        try {
            user = await UserService.getUserProfile(targetUserId);
            if (!user) {
                container.innerHTML = `<div class="p-10 text-center text-gray-500">${locale.t('common.user_not_found') || "User not found"}</div>`;
                return;
            }

            // [FIX] URL Query Param Override (?tab=guestbook)
            // Priority: Query Param > Author Role Default > Default 'ssulwha'
            try {
                // We use window.location.hash to find params in hash router: #/user/123?tab=guestbook
                const hashParts = window.location.hash.split('?');
                if (hashParts.length > 1) {
                    const params = new URLSearchParams(hashParts[1]);
                    const tabParam = params.get('tab');
                    if (tabParam && ['works', 'ssulwha', 'guestbook', 'info'].includes(tabParam)) {
                        activeTab = tabParam;
                    }
                } else if (user.role === 'author' && activeTab === 'ssulwha') {
                    // Only apply author default if NO param was present
                    activeTab = 'works';
                }
            } catch (e) {
                console.warn("Param parse error", e);
            }

            // Check Follow Status
            if (currentUser && currentUser.uid !== targetUserId) {
                isFollowing = await UserService.checkFollowStatus(currentUser.uid, targetUserId);
            }

            // [REMOVED] syncSocialCounts (Client-side writes blocked by Ironclad Security)
            // The Cloud Function `toggleFollow` now handles Atomic Count Updates.
            // No need to client-side sync.
            /*
            UserService.syncSocialCounts(targetUserId).then(realCounts => {
               ...
            });
            */

            renderLayout();
        } catch (e) {
            console.error(e);
            container.innerHTML = `<div class="p-10 text-center text-red-500">프로필을 불러오는데 실패했습니다.</div>`;
        }
    };

    // [FIX] Hash Change Listener for Tab Switching
    const onHashChange = () => {
        if (!document.body.contains(container)) {
            window.removeEventListener('hashchange', onHashChange);
            return;
        }
        // Re-run logic
        try {
            const hashParts = window.location.hash.split('?');
            if (hashParts.length > 1) {
                const params = new URLSearchParams(hashParts[1]);
                const tabParam = params.get('tab');
                if (tabParam && ['works', 'ssulwha', 'guestbook', 'info'].includes(tabParam)) {
                    if (activeTab !== tabParam) {
                        activeTab = tabParam;
                        renderLayout(false); // Re-render body
                    }
                }
            }
        } catch (e) { }
    };
    window.addEventListener('hashchange', onHashChange);

    // --- Actions ---

    const handleFollowToggle = async () => {
        // [FIX] Always check fresh state (Race Condition Fix)
        const activeUser = window.user;

        if (!activeUser) {
            // [FIX] Use Custom Modal instead of native confirm
            // [FIX] Use Standard AuthRequireModal for consistent UX
            new AuthRequireModal();
            return;
        }
        if (isProcessing) return;
        isProcessing = true;

        const btn = document.querySelector('#btn-follow-action');
        if (btn) btn.disabled = true;

        // Optimistic Update
        const prevIsFollowing = isFollowing;
        const prevFollowerCount = user.followerCount || 0;

        isFollowing = !prevIsFollowing;
        user.followerCount = isFollowing ? prevFollowerCount + 1 : Math.max(0, prevFollowerCount - 1);

        renderLayout(true); // Re-render header immediately

        try {
            if (prevIsFollowing) {
                // Unfollow
                await UserService.unfollowUser(activeUser.uid, targetUserId);
            } else {
                // Follow
                await UserService.followUser(activeUser, user);
            }
        } catch (e) {
            console.error("Follow Action Failed:", e);
            // Rollback
            isFollowing = prevIsFollowing;
            user.followerCount = prevFollowerCount;
            renderLayout(true);
            renderLayout(true);
            new AlertModal('오류 발생', '팔로우 처리 중 문제가 발생했습니다.\n잠시 후 다시 시도해주세요.').render();
        } finally {
            isProcessing = false;
            if (btn) btn.disabled = false;
        }
    };


    // --- Renderers ---

    const renderHeader = () => {
        // ... (Header Rendering Logic - Similar to MyPage but with Follow Button)
        const photoURL = user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`;
        const displayName = user.displayName || 'No Name';
        const roleLabel = getRoleLabel(user.role);

        // Follow Button Logic
        let actionBtn = '';
        if (currentUser && currentUser.uid === user.uid) {
            actionBtn = `<button class="px-6 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg text-sm" onclick="window.router.navigate('/mypage')">${locale.t('profile.mypage')}</button>`;
        } else {
            actionBtn = isFollowing
                ? `<button id="btn-follow-action" class="px-6 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg text-sm hover:bg-gray-200 transition-colors">${locale.t('profile.following')}</button>`
                : `<button id="btn-follow-action" class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-sm transition-colors shadow-sm">${locale.t('profile.follow')}</button>`;
        }

        // Social Counts
        const countsHtml = `
            <div class="flex items-center gap-6 mt-3 mb-4 text-sm select-none">
                <button id="btn-followers" class="flex flex-col items-center group cursor-pointer hover:bg-gray-100 px-3 py-1 rounded-lg transition-colors">
                    <span class="font-bold text-gray-900 text-lg group-hover:text-blue-600">${locale.formatNumber(user.followerCount || 0)}</span>
                    <span class="text-gray-500 text-xs">${locale.t('profile.followers')}</span>
                </button>
                <div class="w-px h-8 bg-gray-200"></div>
                <button id="btn-following" class="flex flex-col items-center group cursor-pointer hover:bg-gray-100 px-3 py-1 rounded-lg transition-colors">
                    <span class="font-bold text-gray-900 text-lg group-hover:text-blue-600">${locale.formatNumber(user.followingCount || 0)}</span>
                    <span class="text-gray-500 text-xs">${locale.t('profile.following_count')}</span>
                </button>
            </div>
        `;

        const section = document.createElement('div');
        section.className = 'px-6 py-8 bg-zinc-50 border-b border-zinc-100 flex flex-col items-center text-center';
        section.innerHTML = `
            <div class="w-24 h-24 rounded-full overflow-hidden mb-4 shadow-lg border-4 border-white bg-gray-200">
                <img src="${photoURL}" class="w-full h-full object-cover" alt="${displayName}">
            </div>
            
            <h2 class="text-xl font-bold text-gray-900 flex items-center justify-center gap-1">
                ${displayName}
                ${user.role === 'author' ? '<span class="material-symbols-outlined text-blue-500 text-sm" title="작가">verified</span>' : ''}
            </h2>
            
            <!-- Badges -->
            ${user.badges && user.badges.length > 0 ? `
                <div class="flex flex-wrap justify-center gap-2 mt-2">
                    ${user.badges.map(b => {
            let icon = 'military_tech';
            let title = 'Medal';
            let color = 'text-amber-500';

            if (b === 'best_reviewer') { icon = 'rate_review'; title = locale.t('badge.best_reviewer'); color = 'text-green-500'; }
            else if (b === 'top_author') { icon = 'history_edu'; title = locale.t('badge.top_author'); color = 'text-purple-500'; }
            else if (b === 'vip') { icon = 'diamond'; title = locale.t('badge.vip'); color = 'text-blue-500'; }
            else if (b === 'donation_king') { icon = 'volunteer_activism'; title = locale.t('badge.donation_king'); color = 'text-pink-500'; }
            else if (b === 'hall_of_fame') { icon = 'account_balance'; title = locale.t('badge.hall_of_fame'); color = 'text-yellow-600'; }

            return `<span class="material-symbols-outlined ${color} text-xl cursor-help animate-bounce-subtle" title="${title}">
                            ${icon}
                        </span>`;
        }).join('')}
                </div>
            ` : ''}
            
            ${countsHtml}

            <p class="text-gray-600 text-sm mb-4 italic">"${user.bio || locale.t('profile.bio_empty')}"</p>

            <div class="flex gap-2">
                ${actionBtn}
            </div>
        `;

        // Bind Events
        const followBtn = section.querySelector('#btn-follow-action');
        if (followBtn && (!currentUser || currentUser.uid !== user.uid)) {
            followBtn.onclick = handleFollowToggle;
        }

        section.querySelector('#btn-followers').onclick = () => {
            UserListModal.show('followers', targetUserId, () => loadData());
        };
        section.querySelector('#btn-following').onclick = () => {
            UserListModal.show('following', targetUserId, () => loadData());
        };

        return section;
    };

    const renderTabNav = () => {
        const nav = document.createElement('div');
        nav.className = 'sticky top-16 bg-white z-40 border-b border-gray-100 flex text-sm font-medium text-gray-500 shadow-sm';

        const tabs = [];
        if (user.role === 'author') {
            tabs.push({ id: 'works', label: locale.t('tab.works'), icon: 'auto_stories' });
        }
        tabs.push({ id: 'ssulwha', label: locale.t('tab.feed'), icon: 'forum' });
        tabs.push({ id: 'guestbook', label: locale.t('tab.guestbook'), icon: 'edit_note' });

        tabs.forEach(tab => {
            const isActive = activeTab === tab.id;
            const btn = document.createElement('button');
            btn.className = `flex-1 py-3 flex items-center justify-center gap-2 transition-colors relative ${isActive ? 'text-indigo-600 font-bold' : 'text-gray-400 hover:text-gray-600'}`;
            btn.innerHTML = `
                <span class="material-symbols-outlined ${isActive ? 'fill-current' : ''}">${tab.icon}</span>
                <span>${tab.label}</span>
                ${isActive ? '<span class="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full mx-8"></span>' : ''}
            `;
            btn.onclick = () => {
                activeTab = tab.id;
                renderLayout(); // Re-render
            };
            nav.appendChild(btn);
        });

        return nav;
    };

    const renderWorks = async () => {
        const worksContainer = document.createElement('div');
        worksContainer.className = 'p-4';

        try {
            const seriesList = await ContentService.getAuthorWorks(user.uid);

            if (seriesList.length === 0) {
                worksContainer.innerHTML = `<div class="text-center text-gray-400 py-10 text-sm">${locale.t('profile.works_empty')}</div>`;
                return worksContainer;
            }

            let html = '<div class="grid grid-cols-3 gap-3">';
            seriesList.forEach(s => {
                html += `
                    <div class="cursor-pointer group" onclick="window.router.navigate('/series/${s.id}')">
                        <div class="aspect-[3/4] bg-gray-200 rounded-lg overflow-hidden mb-2 shadow-sm relative">
                            <img src="${s.coverImage || s.thumbnail || 'https://placehold.co/200x300'}" alt="${s.title}" class="w-full h-full object-cover transition-transform group-hover:scale-105" style="-webkit-touch-callout: none;">
                        </div>
                        <h3 class="text-xs font-bold text-gray-900 truncate">${s.title}</h3>
                    </div>
                `;
            });
            html += '</div>';
            worksContainer.innerHTML = html;

        } catch (e) {
            console.error(e);
            worksContainer.innerHTML = `<div class="text-red-500 text-xs">${locale.t('profile.works_error')}</div>`;
        }
        return worksContainer;
    };

    const renderSsulwha = async () => {
        const container = document.createElement('div');
        container.className = 'p-4 space-y-4 bg-gray-50 min-h-[300px]';

        try {
            // [NEW] Fetch Feeds by Author ID
            const result = await PlazaService.fetchFeeds({ authorId: user.uid, limitCount: 20 });
            const feeds = result.feeds;

            if (feeds.length === 0) {
                container.innerHTML = `
                    <div class="flex flex-col items-center justify-center py-12 text-gray-400">
                        <span class="material-symbols-outlined text-4xl mb-2 text-gray-300">feed</span>
                        <p class="text-sm">${locale.t('profile.feed_empty')}</p>
                    </div>
                `;
                return container;
            }

            feeds.forEach(feed => {
                const card = new FeedCard(feed.id, feed, { isFlow: false });
                container.appendChild(card.render());
            });

            // Note: Pagination not implemented here yet (Load More button needed for full profile)
            // For now, limiting to 20 recent items.

        } catch (e) {
            console.error(e);
            container.innerHTML = `
                <div class="text-center py-10">
                    <p class="text-sm text-red-500 mb-2">${locale.t('profile.feed_error')}</p>
                    <p class="text-xs text-gray-400">${locale.t('profile.feed_error_detail')}</p>
                </div>
            `;
        }
        return container;
    };

    const renderGuestbook = async () => {
        const gbContainer = document.createElement('div');
        gbContainer.className = 'p-4';
        gbContainer.innerHTML = `
            <div class="flex items-center justify-between mb-4 px-2">
                <h3 class="font-bold text-lg">${locale.t('tab.guestbook')} <span class="text-sm font-normal text-gray-500" id="gb-count">0</span></h3>
            </div>
            
            <!-- Input -->
            <div class="bg-gray-50 dark:bg-zinc-800 rounded-2xl p-4 mb-6 transition-all focus-within:ring-2 ring-blue-100 ring-offset-1 relative">
                <textarea id="gb-input" rows="2" class="w-full bg-transparent text-sm resize-none focus:outline-none mb-2 placeholder-gray-400 text-gray-900 dark:text-gray-100" style="font-size: 16px !important;" placeholder="${locale.t('guestbook.input_placeholder')}" ></textarea>
                
                <!-- Emoji Picker Container -->
                <div id="emoji-picker-container" class="hidden absolute top-full left-0 mt-2 z-50 shadow-xl rounded-lg overflow-hidden animate-fade-in-down origin-top-left"></div>

                <!-- Image Preview -->
                <div id="gb-image-preview" class="hidden mb-3 relative w-fit">
                    <img class="h-20 rounded-lg border border-gray-200" src="">
                    <button class="absolute -top-2 -right-2 bg-black/50 hover:bg-black text-white rounded-full p-0.5"><span class="material-symbols-outlined text-xs">close</span></button>
                </div>

                <div class="flex justify-between items-center">
                    <div class="flex gap-2">
                         <label class="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors p-1 rounded-full hover:bg-blue-50" title="사진 첨부">
                            <span class="material-symbols-outlined text-[20px]">image</span>
                            <input type="file" id="gb-file-input" class="hidden" accept="image/*">
                        </label>
                        <button id="btn-emoji" class="text-gray-400 hover:text-yellow-500 transition-colors p-1 rounded-full hover:bg-yellow-50" title="이모지">
                            <span class="material-symbols-outlined text-[20px]">sentiment_satisfied</span>
                        </button>
                        <label class="flex items-center gap-1 cursor-pointer text-xs text-gray-500 hover:text-gray-700 select-none p-1">
                            <input type="checkbox" id="gb-secret" class="accent-blue-600 w-3.5 h-3.5">
                            ${locale.t('guestbook.secret')}
                        </label>
                    </div>
                    <button id="gb-submit" class="bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-all active:scale-95">
                        ${locale.t('guestbook.submit')}
                    </button>
                </div>
            </div>

            <!-- List -->
            <div id="gb-list" class="space-y-4 px-1">
                 <div class="text-center py-4"><span class="loader-spin inline-block w-5 h-5 border-gray-300 border-t-blue-500"></span></div>
            </div>
        `;

        const input = gbContainer.querySelector('#gb-input');
        const fileInput = gbContainer.querySelector('#gb-file-input');
        const previewHas = gbContainer.querySelector('#gb-image-preview');
        const previewImg = previewHas.querySelector('img');
        const previewClose = previewHas.querySelector('button');
        const submitBtn = gbContainer.querySelector('#gb-submit');
        const listEl = gbContainer.querySelector('#gb-list');
        const countEl = gbContainer.querySelector('#gb-count');

        // [NEW] Controls
        const btnEmoji = gbContainer.querySelector('#btn-emoji');
        const emojiContainer = gbContainer.querySelector('#emoji-picker-container');
        const secretCheck = gbContainer.querySelector('#gb-secret');

        let selectedFile = null;
        let picker = null;

        // --- Emoji Picker ---
        btnEmoji.onclick = async (e) => {
            e.stopPropagation();
            if (emojiContainer.classList.contains('hidden')) {
                emojiContainer.classList.remove('hidden');
                if (!picker) {
                    try {
                        const { createPopup } = await import('https://unpkg.com/picmo@latest/dist/index.js?module');
                        // Note: Using unpkg for quick import as npm install might need build config update for ESM.
                        // Ideally we use local node_modules but for this env, direct import is safer for immediate run.
                        // Actually, I installed picmo via npm. Let's try importing from 'picmo';
                        // But wait, esbuild might not bundle it automatically without restart.
                        // Let's stick to CDN for guaranteed immediate functionality or try dynamic import.

                        // Let's try dynamic import from node_modules if possible, typical pattern here:
                        const { createPicker } = await import('picmo');

                        picker = createPicker({
                            rootElement: emojiContainer,
                            showPreview: false,
                            autoFocus: 'search'
                        });

                        picker.addEventListener('emoji:select', selection => {
                            input.value += selection.emoji;
                            input.focus();
                            // Optional: Close on select? Maybe keep open for multi-select
                        });
                    } catch (err) {
                        console.warn("Emoji Picker Load Failed, trying CDN", err);
                        // Fallback
                        try {
                            const { createPicker } = await import('https://unpkg.com/picmo@5.7.6/dist/index.js?module');
                            picker = createPicker({ rootElement: emojiContainer });
                            picker.addEventListener('emoji:select', selection => {
                                input.value += selection.emoji;
                                input.focus();
                            });
                        } catch (e2) {
                            console.error("CDN Failed too", e2);
                            alert(locale.t('guestbook.emoji_error'));
                        }
                    }
                }
            } else {
                emojiContainer.classList.add('hidden');
            }
        };

        // Close picker on outside click
        document.addEventListener('click', (e) => {
            if (!emojiContainer.contains(e.target) && e.target !== btnEmoji && !btnEmoji.contains(e.target)) {
                emojiContainer.classList.add('hidden');
            }
        });


        // --- Input Events ---
        fileInput.onchange = (e) => {
            const f = e.target.files[0];
            if (f) {
                selectedFile = f;
                previewImg.src = URL.createObjectURL(f);
                previewHas.classList.remove('hidden');
            }
        };

        previewClose.onclick = () => {
            selectedFile = null;
            fileInput.value = '';
            previewHas.classList.add('hidden');
        };

        // --- Submit ---
        submitBtn.onclick = async () => {
            const val = input.value.trim();
            if (!val && !selectedFile) return;

            // [FIX] Lazy Auth Check (Handle Race Condition)
            const activeUser = window.user;
            if (!activeUser) {
                // Use SweetAlert2 for nicer UX
                const result = await Swal.fire({
                    title: '로그인이 필요합니다',
                    text: "로그인 페이지로 이동하시겠습니까?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: '이동',
                    cancelButtonText: '취소'
                });

                if (result.isConfirmed) {
                    window.router.navigate('/login');
                }
                return;
            }

            submitBtn.disabled = true;
            submitBtn.innerText = '...';

            try {
                let imageUrl = null;
                if (selectedFile) {
                    // Import upload util
                    const { uploadImage } = await import('../services/storage-service.js');
                    const { convertToWebP } = await import('../utils/image-processor.js');
                    const blob = await convertToWebP(selectedFile);
                    imageUrl = await uploadImage(blob, `guestbook/${user.uid}/${Date.now()}.webp`);
                }

                await UserService.addGuestbookEntry(user.uid, {
                    content: val,
                    imageUrl: imageUrl,
                    fromUid: activeUser.uid,
                    fromName: activeUser.displayName || 'Anonymous',
                    fromPhoto: activeUser.photoURL || null,
                    isSecret: secretCheck.checked // [NEW] Secret Field
                });

                // Reset
                input.value = '';
                previewClose.click();
                secretCheck.checked = false;
                emojiContainer.classList.add('hidden');

            } catch (e) {
                console.error(e);
                alert(locale.t('guestbook.submit_error') + e.message);
            } finally {
                submitBtn.disabled = false;
                submitBtn.innerText = '등록';
            }
        };

        // --- Real-time Subscription ---
        const unsub = UserService.subscribeGuestbook(user.uid, (entries) => {
            countEl.innerText = `${locale.formatNumber(entries.length)}`;
            listEl.innerHTML = '';

            if (entries.length === 0) {
                listEl.innerHTML = `<div class="text-center text-gray-400 text-xs py-8 bg-gray-50 rounded-xl">${locale.t('guestbook.empty')}</div>`;
                return;
            }

            entries.forEach(entry => {
                listEl.appendChild(createGuestbookNode(entry));
            });
        });

        // Cleanup on unmount (Simple hack: observe removal)
        // Ideally should be handled by Page unmount, but pure JS SPA makes it tricky.
        // We rely on full-page re-renders mostly.

        // *** Guestbook Node Logic (With Reply) ***
        const createGuestbookNode = (entry) => {
            const isWriter = currentUser && currentUser.uid === entry.fromUid;
            const isOwner = currentUser && currentUser.uid === user.uid; // Profile Owner
            const isAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'super_admin');
            const canDelete = isWriter || isOwner || isAdmin;

            // [Secret Logic]
            const isSecret = entry.isSecret;
            const canViewSecret = !isSecret || isWriter || isOwner || isAdmin;

            const date = entry.createdAt ? new Date(entry.createdAt.toDate()).toLocaleDateString() : '';

            const el = document.createElement('div');
            el.className = 'bg-white border border-gray-100 rounded-2xl p-4 relative group fade-in shadow-sm hover:shadow-md transition-shadow';

            // Content Rendering
            let contentHTML = '';
            if (canViewSecret) {
                contentHTML = `
                    <div class="mt-1 text-sm text-gray-800 break-words leading-relaxed whitespace-pre-wrap">${entry.content || ''}</div>
                    ${entry.imageUrl ? `<div class="mt-2 rounded-lg overflow-hidden border border-gray-100 max-h-60 w-fit"><img src="${entry.imageUrl}" class="max-h-60 object-contain display-image cursor-pointer"></div>` : ''}
                `;
            } else {
                contentHTML = `
                    <div class="mt-2 p-3 bg-gray-50 rounded-lg flex items-center gap-2 text-gray-400 text-sm select-none">
                        <span class="material-symbols-outlined text-sm">lock</span>
                        ${locale.t('guestbook.secret_content')}
                    </div>
                `;
            }

            el.innerHTML = `
                <div class="flex gap-3">
                    <div class="w-10 h-10 rounded-full bg-gray-100 overflow-hidden shrink-0 cursor-pointer" onclick="window.router.navigate('/user/${entry.fromUid}')">
                         ${entry.fromPhoto ? `<img src="${entry.fromPhoto}" class="w-full h-full object-cover" alt="">` : `<div class="w-full h-full flex items-center justify-center text-xs font-bold text-gray-400">${entry.fromName?.[0]}</div>`}
                    </div>
                    <div class="flex-grow min-w-0">
                        <div class="flex justify-between items-start">
                            <div class="flex items-center gap-1.5">
                                <span class="font-bold text-gray-900 text-sm cursor-pointer hover:underline" onclick="window.router.navigate('/user/${entry.fromUid}')">
                                    ${entry.fromName}
                                </span>
                                ${isSecret ? '<span class="material-symbols-outlined text-[12px] text-gray-400" title="Secret">lock</span>' : ''}
                                <span class="text-[11px] text-gray-400 ml-1">${entry.createdAt ? locale.formatDate(entry.createdAt.toDate(), { dateStyle: 'medium' }) : ''}</span>
                            </div>
                            ${canDelete ? `<button class="text-gray-300 hover:text-red-500 btn-delete"><span class="material-symbols-outlined text-[16px]">close</span></button>` : ''}
                        </div>
                        
                        ${contentHTML}

                        <!-- Reply Action -->
                        ${canViewSecret ? `
                        <div class="mt-3 flex items-center gap-4">
                            <button class="flex items-center gap-1 text-xs text-gray-500 font-bold hover:text-blue-600 transition-colors btn-reply-toggle">
                                ${locale.t('guestbook.reply')} <span class="reply-count">${entry.replyCount || 0}</span>
                            </button>
                        </div>
                        ` : ''}

                         <!-- Reply Section (Hidden by default) -->
                        <div class="reply-section hidden mt-3 bg-gray-50 rounded-xl p-3 animate-fade-in-down">
                            <div class="reply-list space-y-3 mb-3"></div>
                            
                            <!-- Reply Input -->
                            <div class="flex gap-2">
                                <input type="text" class="reply-input flex-grow bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-base focus:border-blue-500 outline-none" style="font-size: 16px !important;" placeholder="${locale.t('guestbook.reply_placeholder')}" ${!currentUser ? 'disabled' : ''}>
                                <button class="reply-submit text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">${locale.t('guestbook.submit')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;


            // Delete
            if (canDelete) {
                el.querySelector('.btn-delete').onclick = async () => {
                    if (confirm(locale.t('guestbook.delete_confirm'))) {
                        await UserService.deleteGuestbookEntry(user.uid, entry.id);
                        // No need to remove manually, subscription handles it
                    }
                };
            }

            // Image Zoom
            const img = el.querySelector('.display-image');
            if (img) img.onclick = () => window.open(entry.imageUrl, '_blank');

            // --- Reply Logic ---
            const replyToggle = el.querySelector('.btn-reply-toggle');
            const replySection = el.querySelector('.reply-section');
            const replyList = el.querySelector('.reply-list');
            const replyInput = el.querySelector('.reply-input');
            const replySubmit = el.querySelector('.reply-submit');
            let repliesUnsub = null;

            if (replyToggle) {
                replyToggle.onclick = () => {
                    const isHidden = replySection.classList.contains('hidden');
                    if (isHidden) {
                        replySection.classList.remove('hidden');
                        // Subscribe
                        repliesUnsub = UserService.subscribeGuestbookReplies(user.uid, entry.id, (replies) => {
                            replyList.innerHTML = '';
                            if (el.querySelector('.reply-count')) el.querySelector('.reply-count').innerText = replies.length;

                            replies.forEach(r => {
                                const rEl = document.createElement('div');
                                rEl.className = "flex gap-2 text-xs";
                                const rCanDelete = currentUser && (currentUser.uid === r.fromUid || isOwner || isAdmin);

                                rEl.innerHTML = `
                                    <div class="w-6 h-6 rounded-full bg-gray-200 overflow-hidden shrink-0 mt-0.5">
                                        ${r.fromPhoto ? `<img src="${r.fromPhoto}" class="w-full h-full object-cover" alt="">` : `<div class="w-full h-full flex items-center justify-center text-[8px] font-bold text-gray-500">${r.fromName?.[0]}</div>`}
                                    </div>
                                    <div class="flex-grow">
                                        <div class="flex items-center gap-1">
                                            <span class="font-bold text-gray-700">${r.fromName}</span>
                                            ${rCanDelete ? `<button class="text-gray-300 hover:text-red-500 btn-reply-delete ml-auto"><span class="material-symbols-outlined text-[12px]">close</span></button>` : ''}
                                        </div>
                                        <p class="text-gray-600 mt-0.5 break-all">${r.content}</p>
                                    </div>
                                `;

                                if (rCanDelete) {
                                    rEl.querySelector('.btn-reply-delete').onclick = async () => {
                                        if (confirm(locale.t('guestbook.confirm_delete_reply'))) {
                                            await UserService.deleteGuestbookReply(user.uid, entry.id, r.id);
                                        }
                                    };
                                }
                                replyList.appendChild(rEl);
                            });
                        });
                    } else {
                        replySection.classList.add('hidden');
                        if (repliesUnsub) repliesUnsub();
                    }
                };
            }

            replySubmit.onclick = async () => {
                const val = replyInput.value.trim();
                if (!val) return;
                replySubmit.disabled = true;
                try {
                    await UserService.addGuestbookReply(user.uid, entry.id, {
                        content: val,
                        fromUid: currentUser.uid,
                        fromName: currentUser.displayName || 'Anonymous',
                        fromPhoto: currentUser.photoURL || null
                    });
                    replyInput.value = '';
                } catch (e) {
                    alert(e.message);
                } finally {
                    replySubmit.disabled = false;
                }
            };

            return el;
        };

        return gbContainer;
    };

    const renderLayout = (headerOnly = false) => {
        if (headerOnly && container.firstChild) {
            const h = renderHeader();
            container.firstChild.replaceWith(h);
            return;
        }

        container.innerHTML = '';
        container.appendChild(renderHeader());
        container.appendChild(renderTabNav());

        // Body Content
        const bodyDiv = document.createElement('div');
        container.appendChild(bodyDiv);

        if (activeTab === 'works') {
            renderWorks().then(el => bodyDiv.appendChild(el));
        } else if (activeTab === 'ssulwha') {
            renderSsulwha().then(el => bodyDiv.appendChild(el));
        } else if (activeTab === 'guestbook') {
            renderGuestbook().then(el => bodyDiv.appendChild(el));
        }
    };

    const getRoleLabel = (role) => {
        if (role === 'admin' || role === 'super_admin') return '관리자';
        if (role === 'author') return '작가';
        return '독자';
    };

    // --- Init ---
    await loadData();

    return container;
};
